import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { 
  MessageCircle, Play, Clock, Flame, Heart, 
  Settings, Volume2, Sparkles, ArrowRight, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Lira avatars based on voice gender
const LIRA_AVATARS = {
  female: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=400&h=400&fit=crop&crop=face',
  male: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop&crop=face'
};

// Mood to emoji mapping
const MOOD_EMOJIS = {
  excited: '✨',
  happy: '😊',
  proud: '🌟',
  missed_you: '💜',
  worried: '🥺'
};

// Relationship level badges
const RELATIONSHIP_BADGES = {
  new: { label: 'Conhecendo', color: 'bg-blue-100 text-blue-600', icon: '👋' },
  acquaintance: { label: 'Conhecidos', color: 'bg-green-100 text-green-600', icon: '🌱' },
  friend: { label: 'Amigos', color: 'bg-purple-100 text-purple-600', icon: '🤝' },
  close_friend: { label: 'Próximos', color: 'bg-pink-100 text-pink-600', icon: '💫' },
  best_friend: { label: 'Melhores Amigos', color: 'bg-amber-100 text-amber-600', icon: '💛' }
};

const LiraHome = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [startingSession, setStartingSession] = useState(false);
  const [homeData, setHomeData] = useState(null);
  const [voiceGender, setVoiceGender] = useState('female');
  const [isLiraSpeaking, setIsLiraSpeaking] = useState(false);

  useEffect(() => {
    fetchLiraHome();
    fetchVoicePreference();
  }, []);

  const fetchVoicePreference = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/personality`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVoiceGender(response.data.voice_gender || 'female');
    } catch (error) {
      console.error('Failed to fetch voice preference:', error);
    }
  };

  const fetchLiraHome = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/companion/home`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHomeData(response.data);
      
      // Auto-speak Lira's greeting after short delay
      setTimeout(() => {
        if (response.data.lira?.greeting) {
          speakGreeting(response.data.lira.greeting);
        }
      }, 500);
    } catch (error) {
      console.error('Failed to fetch Lira home:', error);
      toast.error('Não foi possível carregar. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const speakGreeting = async (text) => {
    // Extract just the target language part (before translation)
    const mainText = text.split('\n(')[0].split('(')[0].trim();
    if (!mainText) return;

    setIsLiraSpeaking(true);
    try {
      // Use Web Speech API for quick response
      if ('speechSynthesis' in window) {
        const utterance = new SpeechSynthesisUtterance(mainText);
        utterance.lang = user?.target_language === 'Spanish' ? 'es-ES' : 
                        user?.target_language === 'French' ? 'fr-FR' :
                        user?.target_language === 'German' ? 'de-DE' : 'en-US';
        utterance.rate = 0.85;
        utterance.onend = () => setIsLiraSpeaking(false);
        utterance.onerror = () => setIsLiraSpeaking(false);
        window.speechSynthesis.speak(utterance);
      }
    } catch (error) {
      console.error('TTS error:', error);
      setIsLiraSpeaking(false);
    }
  };

  const startSession = async (mode = 'continue') => {
    setStartingSession(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/companion/session/start?mode=${mode}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Navigate to chat with the new session
      navigate(`/chat/${response.data.conversation_id}`);
    } catch (error) {
      console.error('Failed to start session:', error);
      toast.error('Não foi possível iniciar. Tente novamente.');
      setStartingSession(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center">
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <motion.div
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 1, repeat: Infinity }}
            className="w-24 h-24 mx-auto mb-4"
          >
            <img 
              src={LIRA_AVATARS[voiceGender]}
              alt="Lira"
              className="w-full h-full rounded-full object-cover border-4 border-vocalira shadow-glow"
            />
          </motion.div>
          <p className="text-dark-muted text-sm">A Lira está chegando...</p>
        </motion.div>
      </div>
    );
  }

  const lira = homeData?.lira || {};
  const userData = homeData?.user || {};
  const context = homeData?.context || {};
  const relationshipBadge = RELATIONSHIP_BADGES[userData.relationship_level] || RELATIONSHIP_BADGES.new;

  return (
    <div className="min-h-screen bg-dark-bg" data-testid="lira-home">
      {/* Minimal Header */}
      <header className="p-4 flex justify-end">
        <Button
          onClick={() => navigate('/settings')}
          variant="ghost"
          size="icon"
          className="rounded-full text-dark-muted hover:text-dark-text"
        >
          <Settings className="w-5 h-5" />
        </Button>
      </header>

      {/* Main Content - Lira Centered */}
      <main className="px-6 pb-8 max-w-lg mx-auto">
        
        {/* Lira Avatar Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          {/* Relationship Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium mb-4 ${relationshipBadge.color}`}
          >
            <span>{relationshipBadge.icon}</span>
            <span>{relationshipBadge.label}</span>
          </motion.div>

          {/* Lira Avatar */}
          <div className="relative inline-block mb-6">
            <motion.div
              animate={isLiraSpeaking ? { scale: [1, 1.05, 1] } : {}}
              transition={{ duration: 0.5, repeat: isLiraSpeaking ? Infinity : 0 }}
              className="relative"
            >
              <img 
                src={LIRA_AVATARS[voiceGender]}
                alt="Lira"
                className="w-32 h-32 rounded-full object-cover border-4 border-dark-card shadow-glow"
              />
              
              {/* Speaking Indicator */}
              {isLiraSpeaking && (
                <motion.div
                  className="absolute -bottom-1 -right-1 bg-vocalira rounded-full p-2 shadow-md"
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 0.3, repeat: Infinity }}
                >
                  <Volume2 className="w-4 h-4 text-white" />
                </motion.div>
              )}
              
              {/* Mood Indicator */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.5, type: "spring" }}
                className="absolute -top-1 -right-1 bg-dark-card rounded-full p-1.5 shadow-md text-lg border border-dark-border"
              >
                {MOOD_EMOJIS[lira.mood] || '✨'}
              </motion.div>
            </motion.div>
          </div>

          {/* Lira's Name */}
          <h1 className="text-2xl font-semibold text-dark-text mb-1">Lira</h1>
          <p className="text-sm text-dark-muted">Sua companheira de {user?.target_language || 'English'}</p>
        </motion.div>

        {/* Greeting Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-dark-card rounded-3xl p-6 shadow-dark-soft border border-dark-border mb-6"
        >
          {/* Greeting Text */}
          <div className="mb-4">
            <p className="text-dark-text text-lg leading-relaxed">
              {lira.greeting?.split('\n')[0] || 'Olá! Vamos praticar?'}
            </p>
            {lira.translation && (
              <p className="text-dark-muted text-sm mt-2 italic">
                {lira.translation}
              </p>
            )}
          </div>

          {/* Listen Again Button */}
          <button
            onClick={() => speakGreeting(lira.greeting)}
            disabled={isLiraSpeaking}
            className="flex items-center gap-2 text-sm text-dark-muted hover:text-vocalira transition-colors"
          >
            <Volume2 className={`w-4 h-4 ${isLiraSpeaking ? 'animate-pulse text-vocalira' : ''}`} />
            <span>{isLiraSpeaking ? 'Falando...' : 'Ouvir novamente'}</span>
          </button>
        </motion.div>

        {/* Main CTA - Start Conversation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="space-y-3"
        >
          {/* Primary: Continue or Start */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => startSession(context.has_yesterday ? 'continue' : 'new')}
            disabled={startingSession}
            className="w-full bg-vocalira-gradient text-white rounded-2xl p-5 shadow-glow flex items-center justify-between group"
            data-testid="start-conversation-btn"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center group-hover:bg-white/30 transition-colors">
                <MessageCircle className="w-6 h-6 text-white" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-lg">
                  {context.has_yesterday ? 'Continuar conversa' : 'Conversar'}
                </p>
                <p className="text-white/70 text-sm">
                  {context.has_yesterday 
                    ? `Sobre: ${context.yesterday_topics?.[0] || 'nossa conversa'}`
                    : 'Lira vai iniciar a conversa'
                  }
                </p>
              </div>
            </div>
            <motion.div
              animate={startingSession ? { rotate: 360 } : { x: [0, 5, 0] }}
              transition={startingSession ? { duration: 1, repeat: Infinity } : { duration: 1.5, repeat: Infinity }}
            >
              {startingSession ? (
                <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <ArrowRight className="w-6 h-6 text-white" />
              )}
            </motion.div>
          </motion.button>

          {/* Secondary: Quick Practice */}
          <motion.button
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            onClick={() => startSession('practice')}
            disabled={startingSession}
            className="w-full bg-dark-card border border-dark-border rounded-2xl p-4 flex items-center justify-between group hover:border-vocalira/30 transition-colors"
            data-testid="quick-practice-btn"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-cyan/20 rounded-xl flex items-center justify-center">
                <Clock className="w-5 h-5 text-cyan" />
              </div>
              <div className="text-left">
                <p className="font-medium text-dark-text">Prática rápida</p>
                <p className="text-dark-muted text-xs">5 minutos de conversação</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-dark-muted group-hover:text-vocalira transition-colors" />
          </motion.button>
        </motion.div>

        {/* Stats Row */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="grid grid-cols-3 gap-3 mt-6"
        >
          {/* Streak */}
          <div className="bg-dark-card rounded-2xl p-4 text-center border border-dark-border">
            <div className="w-10 h-10 bg-orange-500/20 rounded-xl flex items-center justify-center mx-auto mb-2">
              <Flame className={`w-5 h-5 ${userData.streak > 0 ? 'text-orange-400' : 'text-orange-400/50'}`} />
            </div>
            <p className="text-xl font-bold text-dark-text">{userData.streak || 0}</p>
            <p className="text-xs text-dark-muted">dias seguidos</p>
          </div>

          {/* Conversations */}
          <div className="bg-dark-card rounded-2xl p-4 text-center border border-dark-border">
            <div className="w-10 h-10 bg-vocalira/20 rounded-xl flex items-center justify-center mx-auto mb-2">
              <MessageCircle className="w-5 h-5 text-vocalira" />
            </div>
            <p className="text-xl font-bold text-dark-text">{userData.total_conversations || 0}</p>
            <p className="text-xs text-dark-muted">conversas</p>
          </div>

          {/* Time */}
          <div className="bg-dark-card rounded-2xl p-4 text-center border border-dark-border">
            <div className="w-10 h-10 bg-cyan/20 rounded-xl flex items-center justify-center mx-auto mb-2">
              <Clock className="w-5 h-5 text-cyan" />
            </div>
            <p className="text-xl font-bold text-dark-text">{userData.total_minutes || 0}</p>
            <p className="text-xs text-dark-muted">minutos</p>
          </div>
        </motion.div>

        {/* Relationship Progress (if not new) */}
        {userData.relationship_level && userData.relationship_level !== 'new' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="mt-6 bg-gradient-to-r from-vocalira/20 to-cyan/20 rounded-2xl p-4 border border-vocalira/30"
          >
            <div className="flex items-center gap-3">
              <Heart className="w-5 h-5 text-pink-400" />
              <div className="flex-1">
                <p className="text-sm font-medium text-dark-text">
                  Vocês são {relationshipBadge.label.toLowerCase()}!
                </p>
                <p className="text-xs text-dark-muted">
                  Continue conversando para fortalecer o vínculo
                </p>
              </div>
              <Sparkles className="w-5 h-5 text-amber-400" />
            </div>
          </motion.div>
        )}

        {/* Quick Links */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="flex justify-center gap-6 mt-8 text-sm text-dark-muted"
        >
          <button 
            onClick={() => navigate('/conversations')}
            className="hover:text-vocalira transition-colors"
          >
            Histórico
          </button>
          <button 
            onClick={() => navigate('/vocabulary')}
            className="hover:text-vocalira transition-colors"
          >
            Vocabulário
          </button>
          <button 
            onClick={() => navigate('/scenarios')}
            className="hover:text-vocalira transition-colors"
          >
            Lições
          </button>
        </motion.div>
      </main>
    </div>
  );
};

export default LiraHome;
